import { type ComponentProps, memo, useMemo } from 'react';

import { CreditIcon } from '@air/next-icons';
import { cn, tailwindVariants } from '@air/utils-tailwind';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { formatCreditUsage, getCreditUsageState } from '~/components/CreditUsageMeter/utils';
import { useAnimatedNumber } from '~/hooks/useAnimatedNumber';

const creditUsageMeterCountVariants = tailwindVariants({
  slots: {
    counts: 'tabular-nums',
    remaining: 'rounded-4 py-0.5 font-semibold text-gray-12',
    separator: 'text-gray-10',
    allocated: 'text-gray-10',
  },
  variants: {
    size: {
      default: {
        counts: 'text-14',
        separator: 'mx-1',
      },
      compact: {
        counts: 'text-12',
        separator: 'ml-0.5 mr-1 text-gray-8',
      },
    },
    usageState: {
      default: {
        remaining: '',
      },
      '50%': {
        remaining: '',
      },
      '75%': {
        remaining: 'px-1 bg-amber-8 text-white',
      },
      '90%': {
        remaining: 'px-1 bg-flash-9 text-white',
      },
      depleted: {
        remaining: 'px-1 bg-flash-9 text-white',
      },
    },
  },
});

export type CreditUsageMeterCountProps = Pick<ComponentProps<'div'>, 'className'> & {
  size?: 'default' | 'compact';
} & Pick<CreditUsageStats, 'allocated' | 'used'>;

export const CreditUsageMeterCount = memo(
  ({ allocated, used, size = 'default', className }: CreditUsageMeterCountProps) => {
    const usageState = useMemo(() => getCreditUsageState({ used, allocated }), [allocated, used]);

    const animatedUsedCount = useAnimatedNumber(used.total);

    const { leftSide, separator, rightSide } = useMemo(() => {
      const rightSide = formatCreditUsage(allocated ?? 0, { noDecimals: true });

      if (size !== 'compact') {
        return {
          leftSide: formatCreditUsage(animatedUsedCount ?? used.total, { useLessThanIndicatorForSmallValues: true }),
          separator: 'used of',
          rightSide: `${rightSide} credits`,
        };
      }

      return {
        leftSide: formatCreditUsage(animatedUsedCount ?? used.total, {
          useLessThanIndicatorForSmallValues: true,
        }),
        separator: '/',
        rightSide,
      };
    }, [allocated, animatedUsedCount, size, used.total]);

    if (!allocated) {
      return null;
    }

    return (
      <div className={cn('flex items-center gap-1', className)}>
        {size === 'compact' && <CreditIcon className="size-4 text-gray-12" />}
        <div className={creditUsageMeterCountVariants({ size, usageState }).counts()}>
          <span className={creditUsageMeterCountVariants({ size, usageState }).remaining()}>{leftSide}</span>
          {separator && (
            <span className={creditUsageMeterCountVariants({ size, usageState }).separator()}>{separator}</span>
          )}
          {rightSide && (
            <span className={creditUsageMeterCountVariants({ size, usageState }).allocated()}>{rightSide}</span>
          )}
        </div>
      </div>
    );
  },
);

CreditUsageMeterCount.displayName = 'CreditUsageMeterCount';
